import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { Modal, ModalRef } from "@odx/angular/components/modal";
import { map, startWith } from "rxjs";

export interface AddConfigToProductListRow {
  quantity: number;
  name: string;
  partNumber: string;
}

export interface AddConfigToProductListModalData {
  title: string;
  rows: AddConfigToProductListRow[];
  quantity: number;
}

@Component({
  selector: "app-add-config-to-product-list-modal",
  imports: [CommonModule, ReactiveFormsModule, UiKitModule],
  templateUrl: "./add-config-to-product-list-modal.component.html",
  styleUrl: "./add-config-to-product-list-modal.component.scss",
  standalone: true,
})
export class AddConfigToProductListModalComponent implements Modal<AddConfigToProductListModalData, number> {
  readonly modalRef = inject(ModalRef<AddConfigToProductListModalData, number>);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly rows = this.modalRef.data.rows ?? [];
  protected readonly title = this.modalRef.data.title;
  protected readonly quantityForm = this.formBuilder.nonNullable.group({
    quantity: [this.normalizeQuantity(this.modalRef.data.quantity), [Validators.required, Validators.min(1)]],
  });
  protected readonly quantity = toSignal(
    this.quantityForm.controls.quantity.valueChanges.pipe(
      startWith(this.quantityForm.controls.quantity.value),
      map((value) => this.normalizeQuantity(value)),
    ),
    { initialValue: this.normalizeQuantity(this.quantityForm.controls.quantity.value) },
  );
  protected readonly previewRows = toSignal(
    this.quantityForm.controls.quantity.valueChanges.pipe(
      startWith(this.quantityForm.controls.quantity.value),
      map((value) => this.buildPreviewRows(this.normalizeQuantity(value))),
    ),
    { initialValue: this.buildPreviewRows(this.normalizeQuantity(this.quantityForm.controls.quantity.value)) },
  );

  protected increment() {
    this.quantityForm.patchValue({ quantity: this.quantity() + 1 });
  }

  protected decrement() {
    if (this.quantity() <= 1) {
      return;
    }
    this.quantityForm.patchValue({ quantity: this.quantity() - 1 });
  }

  protected confirm() {
    if (this.quantityForm.invalid) {
      this.quantityForm.controls.quantity.markAsTouched();
      return;
    }

    this.modalRef.close(this.quantity());
  }

  private buildPreviewRows(quantity: number) {
    return this.rows.map((row) => ({
      ...row,
      quantity: row.quantity * quantity,
    }));
  }

  private normalizeQuantity(rawValue: unknown) {
    const quantity = Number(rawValue);
    return Number.isFinite(quantity) ? Math.max(1, Math.floor(quantity)) : 1;
  }
}
